import { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import { Pencil, RefreshCw, TableIcon, Filter, ListChecks, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getRecordsWithJustifications } from "@/lib/api";
import RecordJustificationModal from "./RecordJustificationModal";

function fmtNum(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  // Display minutes-as-decimal in mm:ss (60s) format
  const totalSec = Math.max(0, Math.round(n * 60));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function timeOnly(hms) {
  if (!hms) return "—";
  return hms.slice(0, 5);
}

/** Justified value if any, else the captured value. */
function effectiveValue(record, field) {
  const j = record.justifications?.[field];
  if (j && j.suggested_value !== undefined && j.suggested_value !== null) {
    return { value: Number(j.suggested_value), isAdjusted: true, suggested: Number(j.suggested_value) };
  }
  return { value: Number(record[field] || 0), isAdjusted: false, suggested: null };
}

export default forwardRef(function JustifyRecordsSection({ date, onChanged }, ref) {
  const [data, setData] = useState({ records: [], count: 0, uploaded_at: null, source: null });
  const [loading, setLoading] = useState(false);
  const [showOnlyJustified, setShowOnlyJustified] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // { record, field }
  const [showTable, setShowTable] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getRecordsWithJustifications(date);
      setData(d || { records: [], count: 0 });
    } catch (e) {
      toast.error(`Falha a carregar registos: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const filtered = useMemo(() => {
    let rs = data.records || [];
    if (showOnlyJustified) rs = rs.filter((r) => r.justifications && Object.keys(r.justifications).length > 0);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rs = rs.filter((r) =>
        (r.start_time || "").toLowerCase().includes(s) ||
        (r.name || "").toLowerCase().includes(s)
      );
    }
    return rs;
  }, [data.records, showOnlyJustified, search]);

  const totals = useMemo(() => {
    const real = { cutting_time: 0, between_markers: 0, machine_stopped: 0 };
    const adj = { cutting_time: 0, between_markers: 0, machine_stopped: 0 };
    let anyAdj = false;
    let excludedByAnomaly = 0;
    for (const r of data.records || []) {
      for (const f of ["cutting_time", "between_markers", "machine_stopped"]) {
        real[f] += Number(r[f] || 0);
      }
      // Records inside an operator anomaly window do NOT contribute to the
      // "Ajustado" totals — they are considered removed from the day.
      if (r.in_anomaly) {
        excludedByAnomaly += 1;
        anyAdj = true;
        continue;
      }
      for (const f of ["cutting_time", "between_markers", "machine_stopped"]) {
        const { value, isAdjusted } = effectiveValue(r, f);
        adj[f] += value;
        if (isAdjusted) anyAdj = true;
      }
    }
    return { real, adj, anyAdj, excludedByAnomaly };
  }, [data.records]);

  const justifiedCount = useMemo(
    () => (data.records || []).reduce((acc, r) => acc + Object.keys(r.justifications || {}).length, 0),
    [data.records]
  );

  const hasRecords = (data.count || 0) > 0;

  return (
    <>
      {/* Compact summary card with a button to open the table modal */}
      <Card className="p-4 sm:p-5" data-testid="justify-records-section">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 grid place-items-center shrink-0">
              <Pencil className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold">Linhas do dia · justificações</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Corrige valores capturados (Real → Sugerido) para usar no toggle{" "}
                <span className="text-foreground font-medium">Real / Ajustado</span> do painel.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={() => { setShowTable(true); refresh(); }}
              disabled={!hasRecords}
              data-testid="justify-open-table-btn"
              className="gap-1.5"
            >
              <ListChecks className="h-4 w-4" />
              {hasRecords
                ? `Ver ${data.count} linha${data.count === 1 ? "" : "s"}`
                : "Sem capturas"}
              {justifiedCount > 0 && (
                <Badge variant="outline" className="ml-1 border-amber-400 text-amber-700 dark:text-amber-300 text-[10px] px-1.5 py-0">
                  {justifiedCount} ajustada{justifiedCount === 1 ? "" : "s"}
                </Badge>
              )}
              {totals.excludedByAnomaly > 0 && (
                <Badge variant="outline" className="ml-1 border-red-400 text-red-700 dark:text-red-300 text-[10px] px-1.5 py-0">
                  {totals.excludedByAnomaly} anomalia{totals.excludedByAnomaly === 1 ? "" : "s"}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Summary stats — always visible (4 cards) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <Stat label="Linhas" value={data.count || 0} />
          <Stat label="Justificadas" value={justifiedCount} highlight={justifiedCount > 0 ? "amber" : null} />
          <Stat label="Corte (real)" value={fmtNum(totals.real.cutting_time)} />
          <Stat
            label="Corte (ajustado)"
            value={fmtNum(totals.adj.cutting_time)}
            highlight={totals.anyAdj ? "amber" : null}
          />
        </div>
      </Card>

      {/* Full table inside a Dialog so the page stays clean */}
      <Dialog open={showTable} onOpenChange={setShowTable}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 flex flex-col overflow-hidden" data-testid="justify-table-dialog">
          <DialogHeader className="px-5 py-4 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-4 w-4 text-violet-600" />
              Linhas de <span className="tabular-nums">{date}</span>
              <Badge variant="secondary" className="ml-1">{data.count || 0}</Badge>
              {justifiedCount > 0 && (
                <Badge className="bg-amber-100 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300">
                  {justifiedCount} ajustada{justifiedCount === 1 ? "" : "s"}
                </Badge>
              )}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <input
                type="text"
                placeholder="Procurar hora ou plano…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="justify-search-input"
                className="text-xs bg-card border border-border rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary w-48"
              />
              <button
                onClick={() => setShowOnlyJustified((v) => !v)}
                data-testid="justify-filter-btn"
                className={`text-xs px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1 transition-colors ${
                  showOnlyJustified
                    ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900'
                    : 'bg-card border-border text-foreground hover:bg-muted'
                }`}
              >
                <Filter className="h-3 w-3" />
                {showOnlyJustified ? 'Só justificadas' : 'Todas'}
              </button>
              <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="justify-refresh-btn">
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {loading && (data.records || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-12 text-center">A carregar…</div>
            ) : (data.records || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-12 text-center">
                <TableIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
                Sem capturas para este dia.
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground py-12 text-center">
                Nenhuma linha corresponde ao filtro.
              </div>
            ) : (
              <table className="w-full text-xs border-separate border-spacing-0" data-testid="justify-records-table">
                <thead className="sticky top-0 z-10 bg-card">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium border-b border-border">#</th>
                    <th className="px-3 py-2 font-medium border-b border-border">Hora</th>
                    <th className="px-3 py-2 font-medium border-b border-border hidden md:table-cell">Plano</th>
                    <th className="px-3 py-2 font-medium border-b border-border text-right">Corte (mm:ss)</th>
                    <th className="px-3 py-2 font-medium border-b border-border text-right">Entre (mm:ss)</th>
                    <th className="px-3 py-2 font-medium border-b border-border text-right">Parada (mm:ss)</th>
                    <th className="px-3 py-2 font-medium border-b border-border text-right w-12">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => {
                    const ct = effectiveValue(r, "cutting_time");
                    const bm = effectiveValue(r, "between_markers");
                    const ms = effectiveValue(r, "machine_stopped");
                    const hasAny = ct.isAdjusted || bm.isAdjusted || ms.isAdjusted;
                    const inAnom = !!r.in_anomaly;
                    return (
                      <tr
                        key={r.start_time + idx}
                        className={`group transition-colors ${
                          inAnom
                            ? 'bg-red-50/60 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30'
                            : hasAny
                              ? 'bg-amber-50/40 dark:bg-amber-950/10 hover:bg-muted/40'
                              : 'hover:bg-muted/40'
                        }`}
                        data-testid={`justify-row-${r.start_time}`}
                      >
                        <td className="px-3 py-1.5 border-b border-border tabular-nums text-muted-foreground">{idx + 1}</td>
                        <td className="px-3 py-1.5 border-b border-border tabular-nums font-medium">
                          <div className="flex items-center gap-1.5">
                            {timeOnly(r.start_time)} – {timeOnly(r.end_time)}
                            {inAnom && (
                              <Badge variant="outline" className="border-red-400 text-red-700 dark:text-red-300 text-[9px] px-1 py-0">
                                Anomalia
                              </Badge>
                            )}
                            {hasAny && !inAnom && (
                              <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[9px] px-1 py-0">
                                Ajustada
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 border-b border-border text-muted-foreground hidden md:table-cell truncate max-w-[180px]">
                          {r.name || "—"}
                        </td>
                        <CellWithEdit
                          real={r.cutting_time}
                          value={ct}
                          onEdit={() => setEditing({ record: r, field: "cutting_time" })}
                          testid={`cell-cutting-${r.start_time}`}
                        />
                        <CellWithEdit
                          real={r.between_markers}
                          value={bm}
                          onEdit={() => setEditing({ record: r, field: "between_markers" })}
                          testid={`cell-between-${r.start_time}`}
                        />
                        <CellWithEdit
                          real={r.machine_stopped}
                          value={ms}
                          onEdit={() => setEditing({ record: r, field: "machine_stopped" })}
                          testid={`cell-stopped-${r.start_time}`}
                        />
                        <td className="px-3 py-1.5 border-b border-border text-right">
                          <button
                            onClick={() => setEditing({ record: r, field: "cutting_time" })}
                            data-testid={`justify-edit-${r.start_time}`}
                            className="opacity-60 hover:opacity-100 text-amber-600 inline-flex items-center justify-center h-7 w-7 rounded hover:bg-amber-500/10"
                            title="Reportar anomalia / ajustar"
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RecordJustificationModal
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        date={date}
        record={editing?.record}
        defaultField={editing?.field}
        onSaved={() => { refresh(); onChanged?.(); }}
      />
    </>
  );
});

function Stat({ label, value, highlight }) {
  const cls =
    highlight === "amber"
      ? "bg-amber-50/60 border-amber-300 dark:bg-amber-950/30 dark:border-amber-900"
      : "bg-muted/40 border-border";
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${highlight === "amber" ? "text-amber-700 dark:text-amber-400" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function CellWithEdit({ real, value, onEdit, testid }) {
  return (
    <td
      className="px-3 py-1.5 border-b border-border text-right tabular-nums cursor-pointer hover:bg-primary/5"
      onClick={onEdit}
      data-testid={testid}
      title="Clique para ajustar"
    >
      {value.isAdjusted ? (
        <span className="inline-flex flex-col items-end leading-tight">
          <span className="text-[9px] line-through text-muted-foreground">{fmtNum(real)}</span>
          <span className="font-semibold text-amber-700 dark:text-amber-400">{fmtNum(value.value)}</span>
        </span>
      ) : (
        <span>{fmtNum(value.value)}</span>
      )}
    </td>
  );
}
